import request from '~/utils/request';

import {
  SET_DASHBOARD_WIDGETS_DATA,
  SET_DASHBOARD_CONTACTS_DATA,
  SET_DASHBOARD_ENGAGEMENT_DATA,
  SET_DASHBOARD_TRACKING_ASSETS_DATA,
  SET_DASHBOARD_CHART_DATA,
  SET_LIMIT_MODAL,
  SET_DASHBOARD_WIDGETS_SPENDINGS,
} from './action-types';

const DASHBOARD_ENGAGEMENT_TABLE_FETCH_SIZE = 500;

const getDashboardTablePayload = (response) => response?.data?.data || {};

const getDashboardTableRows = (payload) => (Array.isArray(payload?.rows) ? payload.rows : []);

const fetchDashboardTableData = async (endpoint, params = {}) => {
  const requestParams = {
    ...params,
    page: 1,
    pageSize: DASHBOARD_ENGAGEMENT_TABLE_FETCH_SIZE,
  };
  const firstResponse = await request.getV2(endpoint, requestParams);
  const firstPayload = getDashboardTablePayload(firstResponse);
  const firstRows = getDashboardTableRows(firstPayload);
  const pageCount = Math.max(Number(firstPayload.pageCount) || 0, firstRows.length ? 1 : 0);

  if (pageCount <= 1) return firstPayload;

  const rows = [...firstRows];

  for (let page = 2; page <= pageCount; page += 1) {
    // The UI sorts locally, so every API page must be represented before pagination is applied.
    const response = await request.getV2(endpoint, {
      ...requestParams,
      page,
    });
    rows.push(...getDashboardTableRows(getDashboardTablePayload(response)));
  }

  return {
    ...firstPayload,
    rows,
    page: 1,
    pageSize: rows.length || firstPayload.pageSize || DASHBOARD_ENGAGEMENT_TABLE_FETCH_SIZE,
    total: Number(firstPayload.total) || rows.length,
    pageCount: rows.length ? 1 : 0,
    from: rows.length ? 1 : 0,
    to: rows.length,
  };
};

const fetchDashboardWidgets = (params) => async (dispatch) => {
  try {
    const dashboard = await request.get('dashboard/widgets', params);
    dispatch({ type: SET_DASHBOARD_WIDGETS_DATA, payload: dashboard.data.data });
    return dashboard;
  } catch (error) {
    return error;
  }
};

const fetchDashboardSpendings = (params) => async (dispatch) => {
  try {
    const dashboardSpendings = await request.get('dashboard/widgets', params);
    dispatch({ type: SET_DASHBOARD_WIDGETS_SPENDINGS, payload: dashboardSpendings.data.data });
    return dashboardSpendings;
  } catch (error) {
    return error;
  }
};

const fetchDashboardContacts = (params = {}) => async (dispatch) => {
  try {
    const contacts = await request.getV2('dashboard/contacts', {
      ...(params.refreshCache ? { refreshCache: true } : {}),
    });
    dispatch({ type: SET_DASHBOARD_CONTACTS_DATA, payload: contacts.data.data });
    return contacts;
  } catch (error) {
    dispatch({ type: SET_DASHBOARD_CONTACTS_DATA, payload: null });
    return error;
  }
};

const fetchDashboardEngagement = (params = {}) => async (dispatch) => {
  try {
    const tableParams = {
      ...(params.refreshCache ? { refreshCache: true } : {}),
    };
    const [stats, trackingPerformance, recentResponses] = await Promise.allSettled([
      request.getV2('dashboard/engagement/stats', params),
      fetchDashboardTableData('dashboard/engagement/tracking-performance', {
        ...tableParams,
        duration: 'all',
      }),
      fetchDashboardTableData('dashboard/engagement/recent-responses', {
        ...tableParams,
        duration: 'last 7 days',
      }),
    ]);
    const statsPayload = stats.status === 'fulfilled' ? stats.value?.data?.data : null;
    const trackingPayload = trackingPerformance.status === 'fulfilled' ? trackingPerformance.value : null;
    const recentPayload = recentResponses.status === 'fulfilled' ? recentResponses.value : null;
    const isDashboardCardsDisabled = Boolean(
      statsPayload?.isDashboardCardsDisabled
        || trackingPayload?.isDashboardCardsDisabled
        || recentPayload?.isDashboardCardsDisabled
    );
    const isSampleData = Boolean(
      statsPayload?.isSampleData || trackingPayload?.isSampleData || recentPayload?.isSampleData
    );
    const payload = {
      stats: statsPayload,
      trackingPerformance: trackingPayload,
      recentResponses: recentPayload,
      lastRefreshedAt: statsPayload?.lastRefreshedAt || new Date().toISOString(),
      isDashboardCardsDisabled,
      isSampleData,
    };

    dispatch({ type: SET_DASHBOARD_ENGAGEMENT_DATA, payload });

    return payload;
  } catch (error) {
    return error;
  }
};

const fetchDashboardTrackingAssets = () => async (dispatch) => {
  try {
    const [qrCodes, phoneNumbers] = await Promise.allSettled([
      request.get('qr-code-instances', { page: 1, pageSize: 5 }),
      request.get('tracking-phone-numbers', { page: 1, pageSize: 5 }),
    ]);
    const payload = {
      qrCodes: qrCodes.status === 'fulfilled' ? qrCodes.value?.data?.data : null,
      phoneNumbers: phoneNumbers.status === 'fulfilled' ? phoneNumbers.value?.data?.data : null,
    };

    dispatch({ type: SET_DASHBOARD_TRACKING_ASSETS_DATA, payload });

    return payload;
  } catch (error) {
    return error;
  }
};

const fetchDashboardChart = (params) => async (dispatch) => {
  try {
    const chart = await request.get('/dashboard/charts', params);
    dispatch({ type: SET_DASHBOARD_CHART_DATA, payload: chart.data.data });
    return chart;
  } catch (error) {
    return error;
  }
};

const setLimitModal = (payload) => async (dispatch) => {
  dispatch({ type: SET_LIMIT_MODAL, payload });
};

export {
  fetchDashboardWidgets,
  fetchDashboardContacts,
  fetchDashboardEngagement,
  fetchDashboardTrackingAssets,
  fetchDashboardChart,
  setLimitModal,
  fetchDashboardSpendings,
};
